import { useEffect, useState } from 'react'
import type { LucideIcon } from 'lucide-react'
import { Ban, BedDouble, CalendarPlus, TrendingDown, TrendingUp } from 'lucide-react'
import type {
  MarketAttentionItem,
  MarketAttentionKind,
  MarketCompetitor,
  MarketDemand,
  MarketNight,
  MarketPriceDomain,
  MarketTone,
} from '@/types/market'

/**
 * Display helpers for the Market Intelligence pages.
 *
 * DISPLAY ONLY. Every price, gap, rank, median, label and sentence on these pages comes from the
 * server (`BuildMarketDashboardMutation`). Nothing here derives a figure a reader sees. The one
 * piece of arithmetic that touches a price is `rulerFraction`, which turns a price into a CSS
 * position on the server's own price ruler (`priceDomain`) and is never printed: the same
 * precedent as the soft-dates bar width.
 */

// ─── Colour: identity, the server's judgement, and nothing else ──────────────────────────────────

/** The server's `tone`, as classes. Never applied to a raw position: cheaper is a question, not a verdict. */
export const TONE_STYLE: Record<
  MarketTone,
  { accent: string; edge: string; soft: string; ring: string; text: string; icon: string; tick: string }
> = {
  positive: {
    accent: 'bg-emerald-500',
    edge: 'border-l-emerald-500',
    soft: 'bg-emerald-50',
    ring: 'ring-emerald-200',
    text: 'text-emerald-700',
    icon: 'text-emerald-600',
    tick: '#10b981',
  },
  negative: {
    accent: 'bg-rose-500',
    edge: 'border-l-rose-500',
    soft: 'bg-rose-50',
    ring: 'ring-rose-200',
    text: 'text-rose-700',
    icon: 'text-rose-600',
    tick: '#f43f5e',
  },
  warning: {
    accent: 'bg-amber-500',
    edge: 'border-l-amber-500',
    soft: 'bg-amber-50',
    ring: 'ring-amber-200',
    text: 'text-amber-700',
    icon: 'text-amber-600',
    tick: '#f59e0b',
  },
  neutral: {
    accent: 'bg-slate-400',
    edge: 'border-l-slate-300',
    soft: 'bg-slate-50',
    ring: 'ring-slate-200',
    text: 'text-slate-700',
    icon: 'text-slate-500',
    tick: '#94a3b8',
  },
}

/**
 * Market demand, by colour AND by height, so the lane reads without colour. Chips use the
 * design system's impact-badge classes.
 */
export const DEMAND_STYLE: Record<MarketDemand, { bar: string; height: string; chip: string; dot: string }> = {
  quiet: { bar: 'bg-slate-200', height: '25%', chip: 'bg-slate-100 text-slate-700', dot: 'bg-slate-300' },
  normal: { bar: 'bg-slate-300', height: '50%', chip: 'bg-slate-100 text-slate-700', dot: 'bg-slate-400' },
  busy: { bar: 'bg-amber-400', height: '75%', chip: 'bg-amber-100 text-amber-700', dot: 'bg-amber-400' },
  peak: { bar: 'bg-orange-500', height: '100%', chip: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500' },
  unknown: { bar: 'bg-transparent', height: '0%', chip: 'bg-slate-50 text-slate-500', dot: 'bg-transparent' },
}

/** One icon per "worth a look" kind. Exhaustive, so a new kind fails `tsc` until it has one. */
export const ATTENTION_ICON: Record<MarketAttentionKind, LucideIcon> = {
  market_sold_out: Ban,
  cheap_while_market_fills: TrendingUp,
  cheapest_on_busy_night: BedDouble,
  priced_above_everyone: TrendingDown,
  market_jump_no_event: CalendarPlus,
}

/** Chart and lineup colours (SVG and inline styles need hex, not classes). */
export const MARKET_COLORS = {
  you: '#0d9488',
  band: '#cbd5e1',
  bandEdge: '#94a3b8',
  median: '#64748b',
  competitor: '#475569',
  weekend: '#f1f5f9',
  hover: '#e2e8f0',
  grid: '#e2e8f0',
  attention: '#f59e0b',
  up: '#059669',
  down: '#e11d48',
} as const

// ─── Nights ──────────────────────────────────────────────────────────────────────────────────────

type AttentionRange = Pick<MarketAttentionItem, 'startDate' | 'endDate'>
type AttentionCardLike = AttentionRange & {
  tone: MarketTone
  supporting?: AttentionRange[]
}

/** Does a card cover this night, by its own range or by a finding folded into it? */
export function cardCovers(item: AttentionRange & { supporting?: AttentionRange[] }, date: string): boolean {
  if (item.startDate <= date && date <= item.endDate) return true
  return (item.supporting ?? []).some((s) => s.startDate <= date && date <= s.endDate)
}

/**
 * Which listed nights sit inside a "worth a look" card (its own nights or a folded-in finding's),
 * and in the card's tone. The server puts a night on at most one card. ISO dates compare as strings,
 * so no date arithmetic is needed.
 */
export function attentionTones(
  nights: Pick<MarketNight, 'date'>[],
  items: AttentionCardLike[]
): Map<string, MarketTone> {
  const tones = new Map<string, MarketTone>()
  for (const night of nights) {
    const item = items.find((i) => cardCovers(i, night.date))
    if (item) tones.set(night.date, item.tone)
  }
  return tones
}

/** The "worth a look" card that covers a night, if any. */
export function attentionFor<T extends AttentionRange & { supporting?: AttentionRange[] }>(
  date: string,
  items: T[]
): T | undefined {
  return items.find((i) => cardCovers(i, date))
}

/**
 * The night the page opens on: `?night=` when it is a listed night, else the first "worth a look"
 * item's first listed night, else the first night.
 */
export function initialNightDate(
  nights: Pick<MarketNight, 'date'>[],
  items: Pick<MarketAttentionItem, 'startDate' | 'endDate'>[],
  search: string
): string | null {
  if (nights.length === 0) return null
  const listed = new Set(nights.map((n) => n.date))
  const requested = new URLSearchParams(search).get('night')
  if (requested && listed.has(requested)) return requested
  for (const item of items) {
    const first = nights.find((n) => item.startDate <= n.date && n.date <= item.endDate)
    if (first) return first.date
  }
  return nights[0].date
}

/** Reflects the selected night in `?night=` without a visit: Inertia's history state is kept. */
export function replaceNightParam(date: string) {
  if (typeof window === 'undefined') return
  const url = new URL(window.location.href)
  if (url.searchParams.get('night') === date) return
  url.searchParams.set('night', date)
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
}

// ─── Competitors ─────────────────────────────────────────────────────────────────────────────────

/** marketPropertyId → the badge number (1..n in compset order) used by every section. */
export function badgeNumbers(competitors: Pick<MarketCompetitor, 'marketPropertyId'>[]): Map<number, number> {
  return new Map(competitors.map((c, i) => [c.marketPropertyId, i + 1]))
}

// ─── Compset ─────────────────────────────────────────────────────────────────────────────────────

/** Pasted text to Booking.com URLs: one per line, or separated by spaces or commas. */
export function parseUrls(text: string): string[] {
  return text
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
}

// ─── The price ruler ─────────────────────────────────────────────────────────────────────────────

/**
 * Where a price sits on the server's price ruler, as a CSS percentage (0 to 100, clamped).
 * Layout geometry only: the result positions a dot and is never shown as a number.
 */
export function rulerFraction(value: number, domain: Pick<MarketPriceDomain, 'min' | 'max'>): number {
  const span = domain.max - domain.min
  if (!(span > 0)) return 50
  const fraction = ((value - domain.min) / span) * 100
  if (fraction < 0) return 0
  if (fraction > 100) return 100
  return fraction
}

// ─── Media ───────────────────────────────────────────────────────────────────────────────────────

function useMediaQuery(query: string, fallback: boolean): boolean {
  const supported = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  const [matches, setMatches] = useState(() => (supported ? window.matchMedia(query).matches : fallback))

  useEffect(() => {
    if (!supported) return
    const mql = window.matchMedia(query)
    const onChange = () => setMatches(mql.matches)
    onChange()
    mql.addEventListener?.('change', onChange)
    return () => mql.removeEventListener?.('change', onChange)
  }, [query, supported])

  return matches
}

/** Reduced motion: no line draw, no glide. jsdom has no matchMedia, so it reads as "no preference". */
export function usePrefersReducedMotion(): boolean {
  return useMediaQuery('(prefers-reduced-motion: reduce)', false)
}

/** The `lg` breakpoint, where the chart grows lanes and the night panel sits inline. */
export function useIsDesktop(): boolean {
  return useMediaQuery('(min-width: 1024px)', true)
}
